import { useState, useEffect } from "react";
import { RotateCcw, X, Sparkles } from "lucide-react";

/**
 * Wave 9.1 — persistent banner on IntelligenceView summarising what just changed
 * after a profile save. One-click Revert restores the previous combination.
 *
 * Props:
 *  - summary: { roleLabel, seniorityLabel }
 *  - changed: string[]  (subset of ["role_family", "seniority_tier"])
 *  - onRevert: () => Promise<void>
 *  - onDismiss: () => void
 */
export default function RecalibrationBanner({ summary = {}, changed = [], onRevert, onDismiss }) {
  const [reverting, setReverting] = useState(false);
  const [visible, setVisible] = useState(true);

  // Auto-fade after 45s (user can still revert before then)
  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      if (onDismiss) onDismiss();
    }, 45000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const handleRevert = async () => {
    setReverting(true);
    try {
      await onRevert();
    } finally {
      setReverting(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!visible) return null;

  const changedLabel =
    changed.length === 2
      ? "role and seniority"
      : changed.includes("role_family")
        ? "your role"
        : changed.includes("seniority_tier")
          ? "your seniority"
          : "your combination";

  return (
    <div
      className="mb-6 bg-amber-50 border border-amber-700/30 border-l-4 border-l-amber-700 px-4 py-3 flex items-start gap-3 animate-fade-in print:hidden"
      data-testid="recalibration-banner"
      role="status"
      aria-live="polite"
    >
      <Sparkles className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-zinc-950 leading-relaxed">
          <span className="font-semibold">Your Reveal just recalibrated.</span>{" "}
          <span className="text-zinc-700">You changed {changedLabel}.</span>
        </p>
        {/* 2026-05-03 — "Now viewing as <role> · <seniority>" line removed.
            The universal "Viewing as:" strip below the title is the single
            source of truth for the active lens. The banner now only
            confirms the recalibration and offers Revert. */}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={handleRevert}
          disabled={reverting}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-800 hover:text-amber-900 disabled:opacity-50 transition-colors"
          data-testid="recalibration-revert-btn"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} />
          {reverting ? "Reverting..." : "Revert"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-zinc-900 hover:text-zinc-700 transition-colors p-1 -m-1"
          data-testid="recalibration-dismiss-btn"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
